import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators,} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Subscription} from 'rxjs';
import {KnowledgeLevelData} from 'src/app/data-repositories/knowledge-level-data';
import {MoneyRangeData, MoneyRangeValue,} from '../../../../../data-repositories/money-range-data';
import {Assets} from '../../../../../models/assets';
import {Investment} from '../../../../../models/investment';
import {User} from '../../../../../models/user';
import {ValidationInfoService} from '../../../../../repositories/validation-info.service';
import {FocusInvalidService} from '../../../../../services/focus-invalid.service';
import {BusinessDataService} from '../../../../../data-repositories/business-data.service';
import {StagesNavigationService} from '../../../stages-navigation/stages-navigation.service';
import {InfoDialogService} from '../../../../../components/info-dialog/info-dialog.service';
import {InvestmentInfoComponent} from './investment-info/investment-info.component';
import {MonitoringType} from '../../../../../models/monitoring-type';
import {MarginTypeService} from '../../../../../services/margin-type.service';
import {formLog} from '../../../../../services/debug-logger';

@Component({
  standalone: false,
  selector: 'app-investment',
  templateUrl: './investment.component.html',
  styleUrls: ['./investment.component.scss'],
})
export class InvestmentComponent implements OnInit, OnDestroy {
  public investmentModel: Investment = new Investment();
  public assetsModel: Assets = new Assets();
  public userModel: User = new User();
  public yearsTradingList: Array<MoneyRangeValue>;
  public tradesPerYearList: Array<MoneyRangeValue>;
  public knowledgeLevelList: Array<string>;
  public annualRangeList: Array<MoneyRangeValue>;
  public netWorthRangeList: Array<MoneyRangeValue>;
  public netWorthLiquidRangeList: Array<MoneyRangeValue>;

  public assetsForm: FormGroup;
  public submitted: boolean;
  @ViewChild('assetsFormElement') assetsFormElement: ElementRef;
  private subscription: Subscription;
  private formSub = new Subscription();

  constructor(
    knowledgeLevelData: KnowledgeLevelData,
    moneyRangeData: MoneyRangeData,
    private service: BusinessDataService,
    private fromBuilder: FormBuilder,
    private validationService: ValidationInfoService,
    private focusInvalidService: FocusInvalidService,
    private route: ActivatedRoute,
    private infoService: InfoDialogService,
    public navigationService: StagesNavigationService,
    private marginTypeService: MarginTypeService
  ) {
    this.yearsTradingList = moneyRangeData.getYearsTradingData();
    this.tradesPerYearList = moneyRangeData.getTradingsPerYear();
    this.knowledgeLevelList = knowledgeLevelData.getKnowledgeLevelList();
    this.createForm();

    this.submitted = false;

    this.formSub.add(this.service.GetUser().subscribe((s) => {
      this.investmentModel = s.model.investmentModel;
      this.userModel = s.model;
      this.assetsModel = s.model.assetsModel;
      this.patchFormFromModel();

      this.annualRangeList = moneyRangeData.getAnnualMoneyRangeList(
        s.model.baseCurrency
      );
      this.netWorthRangeList = moneyRangeData.getnetWorthMoneyRangeList(
        s.model.baseCurrency
      );
      this.netWorthLiquidRangeList =
        moneyRangeData.getnetWorthLiquidMoneyRangeList(s.model.baseCurrency);
    }));
  }

  public isRequiredAssetField(fieldName: string): boolean {
    return (
      this.assetsForm.controls[fieldName].invalid &&
      (this.assetsForm.controls[fieldName].dirty ||
        this.assetsForm.controls[fieldName].touched ||
        this.submitted) &&
      this.assetsForm.controls[fieldName].errors.required
    );
  }

  isCorrectLiquidNetWorthSelect(): boolean {
    if (!this.assetsModel.liquidNetWorth) {
      return true;
    }

    const currentLiquidNetWorthValue = this.netWorthLiquidRangeList.find(
      (lnw) => lnw.rangeValue === Number(this.assetsModel.liquidNetWorth)
    );
    const currentLiquidNetWorthIndex = this.netWorthLiquidRangeList.indexOf(
      currentLiquidNetWorthValue
    );

    const currentNetWorthValue = this.netWorthRangeList.find(
      (nw) => nw.rangeValue.toString() === this.assetsModel.netWorth
    );
    let currentNetWorthIndex =
      this.netWorthRangeList.indexOf(currentNetWorthValue);

    const maxLiquidNetWorth = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    if (currentNetWorthIndex > maxLiquidNetWorth.length) {
      currentNetWorthIndex = maxLiquidNetWorth.length - 1;
    }

    return (
      maxLiquidNetWorth[currentNetWorthIndex] >= currentLiquidNetWorthIndex
    );
  }

  isCorrectLiquidNetWorth() {
    return (
      this.assetsModel.liquidNetWorth === '' ||
      Number(this.assetsModel.liquidNetWorth) > 1
    );
  }

  isCorrectNetWorth(): boolean {
    return (
      this.assetsModel.netWorth === '' || Number(this.assetsModel.netWorth) > 1
    );
  }

  isCorrectAnnualIncome(): boolean {
    const annual = this.assetsModel.netInCome;
    if (annual === '' || annual == null) {
      return true;
    }

    const income = Number(annual);
    if (isNaN(income)) {
      return true;
    }

    const min = this.userModel.monitoring === MonitoringType.Ireland ? 2 : 1;
    return income > min;
  }

  isCorrectAnnualIncomeForIE(): boolean {
    const isIreland = this.userModel.monitoring === MonitoringType.Ireland;
    const annualIncome = Number(this.assetsModel.netInCome);

    return this.assetsModel.netInCome === '' || !(isIreland && annualIncome <= 2);
  }

  onLiquidNetWorthChanged() {
    if (this.isCorrectLiquidNetWorth()) {
      this.assetsForm.controls.liquidNetWorth.setErrors(null);
    } else {
      this.assetsForm.controls.liquidNetWorth.setErrors({
        incorrectValue: true,
      });
    }
    this.updateUser();
  }

  ngOnInit() {
    this.patchFormFromModel();
    this.formSub.add(this.assetsForm.valueChanges.subscribe(() => this.syncModelFromForm()));

    this.formSub.add(this.route.queryParams.subscribe((params) => {
      if (params.validate) {
        this.submitted = true;
        this.validationService.markAllFieldsAsTouched(this.assetsForm);
        this.focusInvalidService.selectInvalidInput(this.assetsFormElement);
      }
    }));

    this.subscription = this.validationService.onUserUpdate.subscribe(() => {
      this.submitted = true;
      this.validationService.markAllFieldsAsTouched(this.assetsForm);

      this.focusInvalidService.selectInvalidInput(this.assetsFormElement);
      this.validationService.validate(
        this.assetsForm.valid &&
        // this.sourceIsValid() &&
        // !this.hasZeroSource() &&
        // !this.hasEmptyDescription() &&
        // this.isCorrectAnnualIncome() &&
        // this.isCorrectAnnualIncomeForIE() &&
        // this.isCorrectLiquidNetWorth() &&
        this.isCorrectLiquidNetWorthSelect()
        // this.isCorrectNetWorth()
        , 'InvestmentComponent'
      );
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.formSub.unsubscribe();
  }

  public updateUser(): void {
    this.service.updateUserWithoutValidation(this.userModel, () => {
    });
  }

  openInvestmentInfo() {
    this.infoService.show(InvestmentInfoComponent);
  }

  isCashAccount() {
    return !!this.assetsModel.netWorth &&
      !!this.assetsModel.liquidNetWorth &&
      !!this.assetsModel.netInCome &&
      !this.isRequiredAssetField('netWorth') &&
      !this.isRequiredAssetField('liquidNetWorth') &&
      !this.isRequiredAssetField('annualNetIncome') &&
      this.marginTypeService.getMarginType(this.userModel, true, true, true) === 'Cash';
  }

  private createForm() {
    this.assetsForm = this.fromBuilder.group({
      netWorth: new FormControl('', Validators.required),
      liquidNetWorth: new FormControl('', Validators.required),
      annualNetIncome: new FormControl('', Validators.required),
    });
  }

  private patchFormFromModel() {
    if (!this.assetsModel || !this.assetsForm) {
      return;
    }

    this.assetsForm.patchValue(
      {
        netWorth: this.assetsModel.netWorth ?? '',
        liquidNetWorth: this.assetsModel.liquidNetWorth ?? '',
        annualNetIncome: this.assetsModel.netInCome ?? '',
      },
      {emitEvent: false}
    );
    formLog.state('Investment', this.assetsModel, this.assetsForm);
  }

  private syncModelFromForm() {
    if (!this.assetsModel || !this.assetsForm) {
      return;
    }

    const value = this.assetsForm.getRawValue();
    this.assetsModel.netWorth = value.netWorth;
    this.assetsModel.liquidNetWorth = value.liquidNetWorth;
    this.assetsModel.netInCome = value.annualNetIncome;
    formLog.state('Investment', this.assetsModel, this.assetsForm);
  }
}
